/**
 * Field validation utilities for customer portal
 * Provides character limits and pattern validation without UI changes
 */

import { dateUtils } from './dateUtils';
import type { DateValidationRules } from '../types/dateTypes';

// Validation patterns
export const NAME_FIELD_PATTERN = /^[a-zA-ZñÑ\s\-']*$/;
export const OCCUPATION_PATTERN = /^[a-zA-Z0-9ñÑ\s\-.,()&/]*$/;
export const ADDRESS_FIELD_PATTERN = /^[a-zA-Z0-9ñÑ\s\-.,/]*$/;
export const ID_NUMBER_PATTERN = /^[a-zA-Z0-9\s\-/]*$/;

// Character limits
export const NAME_FIELD_MAX_LENGTH = 50;
export const OCCUPATION_MAX_LENGTH = 100;
export const ADDRESS_FIELD_MAX_LENGTH = 100;
export const ID_NUMBER_MAX_LENGTH = 30;
export const EMAIL_MAX_LENGTH = 254;

/**
 * Validate name fields (First Name, Middle Name, Last Name)
 * Max 50 characters, allows letters (including ñ/Ñ), spaces, hyphens, apostrophes
 */
export const validateNameField = (value: string): boolean => {
  if (!value) return true; // Allow empty for optional fields
  if (value.length > NAME_FIELD_MAX_LENGTH) return false;
  return NAME_FIELD_PATTERN.test(value);
};

/**
 * Filter name field input to only allow valid characters
 */
export const filterNameField = (value: string): string => {
  return value.replace(/[^a-zA-ZñÑ\s\-']/g, '');
};

/**
 * Validate occupation field
 * Max 100 characters, allows letters (including ñ/Ñ), numbers, spaces, common punctuation
 */
export const validateOccupationField = (value: string): boolean => {
  if (!value) return true; // Allow empty for validation, required check handled separately
  if (value.length > OCCUPATION_MAX_LENGTH) return false;
  return OCCUPATION_PATTERN.test(value);
};

/**
 * Filter occupation field input to only allow valid characters
 */
export const filterOccupationField = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9ñÑ\s\-.,()&/]/g, '');
};

/**
 * Validate address fields (House/Unit No., Street Name, Subdivision/Village)
 * Max 150 characters, allows letters (including ñ/Ñ), numbers, spaces, comma, period, hyphen, slash
 */
export const validateAddressField = (value: string): boolean => {
  if (!value) return true; // Allow empty for validation, required check handled separately
  if (value.length > ADDRESS_FIELD_MAX_LENGTH) return false;
  return ADDRESS_FIELD_PATTERN.test(value);
};

/**
 * Filter address field input to only allow valid characters
 */
export const filterAddressField = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9ñÑ\s\-.,/]/g, '');
};

/**
 * Validate ID number field
 * Max 30 characters, allows letters, numbers, hyphen, slash, space
 */
export const validateIdNumberField = (value: string): boolean => {
  if (!value) return true; // Allow empty for validation, required check handled separately
  if (value.length > ID_NUMBER_MAX_LENGTH) return false;
  return ID_NUMBER_PATTERN.test(value);
};

/**
 * Filter ID number field input to only allow valid characters
 */
export const filterIdNumberField = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9\s\-/]/g, '');
};

/**
 * Validate email field with character limit
 * Max 254 characters per IETF standard
 */
export const validateEmailLength = (value: string): boolean => {
  return value.length <= EMAIL_MAX_LENGTH;
};

/**
 * Get inline validation error message for a birthdate field.
 * Derives the message from the same minDate/maxDate rules used to disable
 * calendar days, so field-level errors stay aligned with the datepicker
 * instead of relying on separately hardcoded age numbers.
 */
export const getBirthdateFieldError = (
  dateOfBirth: string,
  rules?: DateValidationRules
): string | null => {

  const date = dateUtils.parseDate(dateOfBirth);
  if (!date) return "";

  if (!rules || !dateUtils.isDateDisabled(date, rules)) return null;

  if (rules.minDate && rules.maxDate) {
    const minAge = dateUtils.calculateAge(rules.maxDate);
    const maxAge = dateUtils.calculateAge(rules.minDate);
    return `Age must be between ${minAge} and ${maxAge} years old`;
  }

  return "Please enter a valid date of birth";
};